import {SmallLoader} from '@kit';

import React, {useCallback, useMemo} from 'react';

import {useWikiArticleQuery} from '../../../generated/types';
import {PageTools} from '../../../components/page-tools';
import {getApolloError} from '../../../components/utils';
import {EditHoc} from '../../../components/edit-hoc';
import {Markdown, MarkdownImageContext, MarkdownImageProps} from '../../../components/utils/md';
import {useGalleryImages} from '../../../components/gallery';
import type {WikiArticle, WikiArticleLinkFragment} from '../../../generated/types';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {Link} from '../../../lib/routes';
import {WikiSeo} from '../seo';
import {NotFound} from '../../../components/not-found';
import {OtherTitles} from '../../../components/other-titles';
import {ArticleLinks, WikiInfoboxView, wikiTypeTitles} from '../infobox';
import type {WikiInfobox} from '../infobox';
import {WikiImage, wikiGalleryImages} from './wiki-image';
import {WikiNavboxes} from './wiki-navboxes';
import {CommentTargetType, Comments} from '../../../components/comments';

import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import css from './wiki-view.module.css';

type Props = {
	wikiArticleId: string;
	/** название из списка: при серверной отрисовке выбранная запись ещё неизвестна, тогда берётся название из самой статьи */
	title: string;
};

const ArticleList: React.FC<{title: string; articles: WikiArticleLinkFragment[]}> = ({title, articles}) =>
	articles.length ? (
		<section className={css.related}>
			<h2>{title}</h2>
			<ul>
				{articles.map((article) => {
					const types = wikiTypeTitles(article.types);
					return (
						<li key={article.id}>
							<Link route="wikiArticle" params={{id: article.id}}>
								{article.title}
							</Link>
							{types.length > 0 && (
								<span className={css.relatedTypes}> — {types.join(', ').toLowerCase()}</span>
							)}
						</li>
					);
				})}
			</ul>
		</section>
	) : null;

const CHILD_GROUPS = [
	{title: 'Места', types: [] as string[]},
	{title: 'Организации', types: ['organization']},
	{title: 'Персонажи', types: ['person', 'god']},
	{title: 'Праздники', types: ['holiday']},
	{title: 'События', types: ['era', 'event']},
];

function childGroup(types?: string[] | null) {
	return CHILD_GROUPS.find((group) => group.types.some((type) => types?.includes(type))) || CHILD_GROUPS[0];
}

export const WikiView: React.FC<Props> = ({wikiArticleId, title}) => {
	const {data, loading, error} = useWikiArticleQuery({variables: {id: wikiArticleId}, ssr: true});
	const wikiArticle = data?.wikiArticle;
	const notFound = !loading && !wikiArticle && isNotFoundError(error);
	useNotFound(notFound);

	const images = useMemo(
		() => (wikiArticle ? wikiGalleryImages(wikiArticle.description || '', wikiArticle.infobox as WikiInfobox) : []),
		[wikiArticle],
	);
	useGalleryImages(images);
	const renderImage = useCallback((image: MarkdownImageProps) => <WikiImage image={image} />, []);

	return (
		<article className={dashboardCss.readingWrapper}>
			{wikiArticle && <WikiSeo item={wikiArticle} />}
			{notFound && <NotFoundSeo />}
			<PageTools />
			<h1 className={dashboardCss.mainTitle}>{wikiArticle?.title || title}</h1>
			<div className={dashboardCss.page}>
				{loading && (
					<div className={pageCss.fullSizeBlock}>
						<SmallLoader />
					</div>
				)}
				{notFound ? (
					<NotFound />
				) : (
					error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>
				)}
				{wikiArticle && (
					<div className={css.article}>
						<EditHoc
							load={() => import('../edit-buttons').then((m) => m.EditWiki)}
							editProps={{id: wikiArticle.id}}
						>
							{wikiArticle.ancestors.length > 0 && (
								<nav className={css.breadcrumbs} aria-label="Расположение">
									<ArticleLinks articles={wikiArticle.ancestors} separator=" › " />
								</nav>
							)}
							<WikiInfoboxView article={wikiArticle} />
							<MarkdownImageContext.Provider value={renderImage}>
								<Markdown text={wikiArticle.description || ''} />
							</MarkdownImageContext.Provider>
							{CHILD_GROUPS.map((group) => (
								<ArticleList
									key={group.title}
									title={group.title}
									articles={wikiArticle.children.filter((child) => childGroup(child.types) === group)}
								/>
							))}
							<ArticleList title="Участники" articles={wikiArticle.members} />
							<OtherTitles
								originalTitle={wikiArticle.originalTitle}
								alternativeTitles={wikiArticle.alternativeTitles}
							/>
							<WikiNavboxes article={wikiArticle} />
							<Comments targetType={CommentTargetType.Wiki} targetId={wikiArticle.id} />
						</EditHoc>
					</div>
				)}
			</div>
		</article>
	);
};
